import React from 'react';
import { logErrorToConsole, getBrowserName } from '../utils/util';
import { ErrorBoundary } from 'react-error-boundary';
import { encryptData } from '../utils/encryptionUtils';

import axios from 'src/api/Api';
import { ERROR_BOUNDARY_STATUS } from 'src/constants/appConstants';
import ReportIssueButton from 'src/common/components/floatingButton';

const AppErrorBoundary = ({ children }) => (
  <ErrorBoundary
    fallbackRender={({ error, resetErrorBoundary }) => ( 
      <ReportIssueButton error={error} resetErrorBoundary={resetErrorBoundary}/>
    )}
    onError={async (error, info) => {
      console.error(error);
      const errorMessageLower = error?.message?.toLowerCase() || '';
      const errorNameLower = error?.name?.toLowerCase() || '';
      const errorCodeLower = error?.code || '';

      if (!ERROR_BOUNDARY_STATUS || 
        errorMessageLower === 'request cancelled'|| 
        errorCodeLower === 'err_canceled' ||
        errorNameLower === 'cancelederror' ||
        errorNameLower === 'aborterror' ||
        errorNameLower === 'chunkloaderror' ||
        errorMessageLower.includes('canceled') ||
        errorMessageLower.includes('cancelled') ||
        errorMessageLower.includes('aborted') ||
        errorMessageLower.includes('request aborted') ||
        errorMessageLower.includes('Loading chunk')) {        
        return;
      }
      
      // Check if this is a ChunkLoadError
      const isChunkLoadError = errorMessageLower.includes('loading chunk') || errorNameLower === 'chunkloaderror';
      
      const encryptedPayload = error.requestPayload 
        ? encryptData(error.requestPayload) 
        : '';
      
      // Get page name from localStorage (set by useDynatracePageName hook)
      const dynatracePageName = localStorage.getItem('pageName') || '';
      const currentPage = dynatracePageName || window.location.pathname;
      
      // Extract error type properly
      let errorType = '';
      if (error?.name) {
        errorType = error.name;
      } else if (error?.constructor?.name && error.constructor.name !== 'Object') {
        errorType = error.constructor.name;
      } else if (typeof error === 'string') {
        errorType = 'Error';
      } else if (error?.response) {
        errorType = 'AxiosError';
      } else {
        errorType = 'UnknownError';
      }
      
      // Build comprehensive error message with API response details
      let errorMessage = '';
      
      // If error has response (API error), extract status code and message
      if (error?.response) {
        const statusCode = error.response.status || error.response.data?.statusCode || '';
        const statusMessage = error.response.data?.statusMessage || 
                             error.response.data?.statusDescription || 
                             error.response.statusText || '';
        const originalMessage = error.message || '';
        
        // Combine status code and message
        if (statusCode && statusMessage) {
          errorMessage = `[${statusCode}] ${statusMessage}`;
          if (originalMessage && !originalMessage.includes(statusCode)) {
            errorMessage += ` - ${originalMessage}`;
          }
        } else if (statusCode) {
          errorMessage = `[${statusCode}] ${originalMessage || 'API Error'}`;
        } else if (statusMessage) {
          errorMessage = statusMessage;
        } else {
          errorMessage = originalMessage || error.toString();
        }
      } else {
        // For non-API errors, use standard error message
        errorMessage = error?.message || error?.toString() || 'Unknown error';
      }      
      
      const errorDetails = {
        timestamp: new Date().toLocaleString(),
        sessionId: localStorage.getItem('authToken'),
        userId: localStorage.getItem('NetworkId') || '',
        page: isChunkLoadError ? currentPage+"-Reloaded" : currentPage,
        apiEndPoint: error.apiEndPoint || '', 
        browser: getBrowserName(),
        device: /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
        errorType: errorType,
        errorMessage: errorMessage,
        stackTrace: error?.stack || '',
        requestPayload: encryptedPayload,        
      };
      
      logErrorToConsole('Global ErrorBoundary caught:', errorDetails, info);
      const url = `/occ/v2/${localStorage.getItem(
        'baseSiteId'
      )}/error/logging`;
      logErrorToConsole('Logging error for url:', url, errorDetails);
      try {
        const response = await axios.post(url, errorDetails);
        logErrorToConsole('Error logged for user:', response);
        
        // If it's a ChunkLoadError, reload the application after logging
        if (isChunkLoadError) {                              
          // Give the server a moment to process the log before reloading
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } catch (err) {
        logErrorToConsole('Error logging to backend:', err);
        
        // Still reload for ChunkLoadError even if logging fails
        if (isChunkLoadError) {          
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }
    }}
  >
    {children}
  </ErrorBoundary>
);

export default AppErrorBoundary;